import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { BulkActionsStore } from "@src/components/widgets/bulk-actions/store";
import type {
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  RowSelectionState,
  Updater,
  VisibilityState,
} from "@tanstack/react-table";
import { action, computed, makeObservable, observable } from "mobx";
import type { BodyProps, BodyRow } from "../types";
import { isSameRecord, isSameStringArray, resolveUpdater } from "./helpers";

type BodyStoreParams = Pick<
  BodyProps<BodyRow, unknown>,
  | "tableKey"
  | "allIds"
  | "onSelect"
  | "onAfterSelectAll"
  | "bulkActions"
  | "defaultHiddenColumnIds"
>;

export class DataTableBodyStore {
  @observable.ref
  hiddenColumnIds: string[];

  @observable.ref
  dataColumnOrder: string[];

  @observable.ref
  columnSizing: ColumnSizingState;

  @observable.ref
  expanded: ExpandedState;

  @observable.ref
  pinnedColumnIds: string[];

  @observable.ref
  onSelect?: (ids: string[]) => void;

  readonly bulkActionsStore: BulkActionsStore;

  private readonly hiddenColumnsStorageKey: string;
  private readonly columnOrderStorageKey: string;
  private readonly columnSizingStorageKey: string;
  private readonly columnPinningStorageKey: string;

  constructor(params: BodyStoreParams) {
    makeObservable(this);

    this.hiddenColumnsStorageKey = `table_${params.tableKey}_disabled-cols`;
    this.columnOrderStorageKey = `table_${params.tableKey}_col-order`;
    this.columnSizingStorageKey = `table_${params.tableKey}_col-sizing`;
    this.columnPinningStorageKey = `table_${params.tableKey}_pinned-cols`;

    this.hiddenColumnIds = this.readStoredStringArray(
      this.hiddenColumnsStorageKey,
      params.defaultHiddenColumnIds ?? [],
    );
    this.dataColumnOrder = this.readStoredStringArray(
      this.columnOrderStorageKey,
    );
    this.columnSizing = this.readStoredObject<ColumnSizingState>(
      this.columnSizingStorageKey,
      {},
    );
    this.expanded = {};
    this.pinnedColumnIds = this.readStoredStringArray(
      this.columnPinningStorageKey,
    );
    this.onSelect = params.onSelect;

    this.bulkActionsStore = new BulkActionsStore(
      params.bulkActions ?? [],
      params.allIds,
      (selectedIds) => {
        this.onSelect?.(selectedIds);
      },
      params.onAfterSelectAll,
    );
  }

  @computed
  get rowSelection(): RowSelectionState {
    const selectionMap = new Map<string, boolean>();

    for (const id of this.bulkActionsStore.selectedIdsArray) {
      selectionMap.set(id, true);
    }

    return Object.fromEntries(selectionMap);
  }

  @action.bound
  setOnSelect(onSelect?: (ids: string[]) => void): void {
    this.onSelect = onSelect;
  }

  @action.bound
  syncDataColumnOrder(availableDataColumnIds: string[]): void {
    const nextOrder = [...this.dataColumnOrder];

    const missingIds = availableDataColumnIds.filter(
      (id) => !nextOrder.includes(id),
    );

    for (const missingId of missingIds) {
      const defaultIndex = availableDataColumnIds.indexOf(missingId);

      const nextKnownId = availableDataColumnIds
        .slice(defaultIndex + 1)
        .find((id) => nextOrder.includes(id));

      if (nextKnownId) {
        nextOrder.splice(nextOrder.indexOf(nextKnownId), 0, missingId);
        continue;
      }

      const previousKnownId = availableDataColumnIds
        .slice(0, defaultIndex)
        .toReversed()
        .find((id) => nextOrder.includes(id));

      if (previousKnownId) {
        nextOrder.splice(nextOrder.indexOf(previousKnownId) + 1, 0, missingId);
        continue;
      }

      nextOrder.push(missingId);
    }

    this.setDataColumnOrder(nextOrder);
  }

  @action.bound
  syncHiddenColumnIds(availableDataColumnIds: string[]): void {
    const nextHiddenColumnIds = this.hiddenColumnIds.filter((id) =>
      availableDataColumnIds.includes(id),
    );

    this.setHiddenColumnIds(nextHiddenColumnIds);
  }

  @action.bound
  setDataColumnOrder(columnOrder: string[]): void {
    if (isSameStringArray(this.dataColumnOrder, columnOrder)) {
      return;
    }

    this.dataColumnOrder = columnOrder;
    this.persist(this.columnOrderStorageKey, this.dataColumnOrder);
  }

  @action.bound
  setHiddenColumnIds(hiddenColumnIds: string[]): void {
    if (isSameStringArray(this.hiddenColumnIds, hiddenColumnIds)) {
      return;
    }

    this.hiddenColumnIds = hiddenColumnIds;
    this.persist(this.hiddenColumnsStorageKey, this.hiddenColumnIds);
  }

  @action.bound
  updateColumnVisibility(
    updater: Updater<VisibilityState>,
    availableDataColumnIds: string[],
  ): void {
    const currentVisibility = availableDataColumnIds.reduce<VisibilityState>(
      (acc, id) => {
        acc[id] = !this.hiddenColumnIds.includes(id);
        return acc;
      },
      {},
    );

    const nextVisibility = resolveUpdater(updater, currentVisibility);
    const nextHiddenColumnIds = availableDataColumnIds.filter(
      (id) => nextVisibility[id] === false,
    );

    this.setHiddenColumnIds(nextHiddenColumnIds);
  }

  @action.bound
  updateColumnSizing(updater: Updater<ColumnSizingState>): void {
    const nextColumnSizing = resolveUpdater(updater, this.columnSizing);

    if (isSameRecord(this.columnSizing, nextColumnSizing)) {
      return;
    }

    this.columnSizing = nextColumnSizing;
    this.persist(this.columnSizingStorageKey, this.columnSizing);
  }

  @action.bound
  updateExpanded(updater: Updater<ExpandedState>): void {
    const nextExpanded = resolveUpdater(updater, this.expanded);

    if (
      (typeof this.expanded === "boolean" ||
        typeof nextExpanded === "boolean") &&
      this.expanded === nextExpanded
    ) {
      return;
    }

    if (
      typeof this.expanded !== "boolean" &&
      typeof nextExpanded !== "boolean" &&
      isSameRecord(this.expanded, nextExpanded)
    ) {
      return;
    }

    this.expanded = nextExpanded;
  }

  @action.bound
  updateRowSelection(updater: Updater<RowSelectionState>): void {
    const nextRowSelection = resolveUpdater(updater, this.rowSelection);
    const nextSelectedIds = Object.keys(nextRowSelection).filter(
      (id) => nextRowSelection[id],
    );

    this.bulkActionsStore.setSelectedIds(nextSelectedIds);
    this.onSelect?.(nextSelectedIds);
  }

  @action.bound
  resetColumnPreferences(defaultDataColumnOrder: string[]): void {
    this.setHiddenColumnIds([]);
    this.setDataColumnOrder(defaultDataColumnOrder);
    this.setPinnedColumnIds([]);

    if (Object.keys(this.columnSizing).length === 0) {
      return;
    }

    this.columnSizing = {};
    this.persist(this.columnSizingStorageKey, this.columnSizing);
  }

  @action.bound
  setPinnedColumnIds(pinnedColumnIds: string[]): void {
    if (isSameStringArray(this.pinnedColumnIds, pinnedColumnIds)) {
      return;
    }

    this.pinnedColumnIds = pinnedColumnIds;
    this.persist(this.columnPinningStorageKey, this.pinnedColumnIds);
  }

  @action.bound
  syncPinnedColumnIds(availableDataColumnIds: string[]): void {
    const nextPinnedColumnIds = this.pinnedColumnIds.filter((id) =>
      availableDataColumnIds.includes(id),
    );

    this.setPinnedColumnIds(nextPinnedColumnIds);
  }

  @action.bound
  updateColumnPinning(
    updater: Updater<ColumnPinningState>,
    availableDataColumnIds: string[],
  ): void {
    const currentPinning: ColumnPinningState = {
      left: this.pinnedColumnIds.slice(),
      right: [],
    };

    const nextPinning = resolveUpdater(updater, currentPinning);
    const nextPinnedColumnIds = (nextPinning.left ?? []).filter((id) =>
      availableDataColumnIds.includes(id),
    );

    this.setPinnedColumnIds(nextPinnedColumnIds);
  }

  @action.bound
  handleDragEnd(
    event: DragEndEvent,
    enableColumnOrdering: boolean,
    dataColumnIds: string[],
    dataColumnIdsSet: Set<string>,
  ): void {
    if (!enableColumnOrdering) {
      return;
    }

    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId === overId) {
      return;
    }

    if (!dataColumnIdsSet.has(activeId) || !dataColumnIdsSet.has(overId)) {
      return;
    }

    const currentOrder =
      this.dataColumnOrder.length > 0 ? this.dataColumnOrder : dataColumnIds;

    const oldIndex = currentOrder.indexOf(activeId);
    const nextIndex = currentOrder.indexOf(overId);

    if (oldIndex < 0 || nextIndex < 0) {
      return;
    }

    this.setDataColumnOrder(arrayMove(currentOrder, oldIndex, nextIndex));
  }

  private readStoredStringArray(
    storageKey: string,
    fallback: string[] = [],
  ): string[] {
    if (typeof window === "undefined") {
      return fallback;
    }

    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) {
      return fallback;
    }

    try {
      const parsedValue = JSON.parse(storedValue);

      if (!Array.isArray(parsedValue)) {
        return fallback;
      }

      return parsedValue.filter(
        (value): value is string => typeof value === "string",
      );
    } catch {
      return fallback;
    }
  }

  private readStoredObject<T extends object>(
    storageKey: string,
    fallback: T,
  ): T {
    if (typeof window === "undefined") {
      return fallback;
    }

    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) {
      return fallback;
    }

    try {
      const parsedValue = JSON.parse(storedValue);

      if (parsedValue === null || Array.isArray(parsedValue)) {
        return fallback;
      }

      if (typeof parsedValue !== "object") {
        return fallback;
      }

      return parsedValue as T;
    } catch {
      return fallback;
    }
  }

  private persist(storageKey: string, value: unknown): void {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(value));
  }
}
